import { useNavigate } from 'react-router-dom';
import { TimerManager, ScoreCalculator } from '@examflow/core';
import type { ExamTypeConfig, Question } from '@examflow/core';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  FileText,
  RotateCcw,
  Sparkles,
  Target,
  TrendingUp,
} from 'lucide-react';
import { Badge, Button, Card, CardBody } from '@examflow/ui';
import {
  buildQuizResultViewModel,
  type QuizResultExamContext,
  type QuizResultTrainingContext,
} from '../../features/quiz/quizResultViewModel';
import { isQuizRuntimeHref } from '../../features/quiz/quizRuntimeNavigation';
import { useOptionalQuizRuntimeLaunch } from '../../features/quiz/QuizRuntimeLaunchProvider';

type QuizMode = 'practice' | 'exam';

interface AnswerRecord {
  questionId: string;
  answer: string;
  isCorrect: boolean;
  timeSpent: number;
  sectionId?: string;
  responseMode?: 'closed' | 'open_ended';
  aiScore?: number;
  aiScoreMax?: number;
}

interface QuizResultProps {
  mode: QuizMode;
  answers: AnswerRecord[];
  questions: Question[];
  examConfig: ExamTypeConfig | null;
  timer: TimerManager | null;
  /** Session wall-clock elapsed (ms). Prefers explicit value; falls back to timer / answer sums. */
  elapsedMs?: number | null;
  trainingContext?: QuizResultTrainingContext | null;
  examContext?: QuizResultExamContext | null;
  onRestart: () => void;
}

function resolveDisplayedElapsedMs(
  timer: TimerManager | null,
  elapsedMs: number | null | undefined,
  answers: AnswerRecord[]
): number | null {
  if (elapsedMs != null && elapsedMs > 0) return elapsedMs;
  if (timer) {
    const fromTimer = timer.getElapsed();
    if (fromTimer > 0) return fromTimer;
  }
  const fromAnswers = answers.reduce((sum, answer) => sum + (answer.timeSpent || 0), 0);
  return fromAnswers > 0 ? fromAnswers : null;
}

export function QuizResult({
  mode,
  answers,
  questions,
  examConfig,
  timer,
  elapsedMs = null,
  trainingContext = null,
  examContext = null,
  onRestart,
}: QuizResultProps) {
  const navigate = useNavigate();
  const { launchQuizRuntime } = useOptionalQuizRuntimeLaunch();
  const resultModel = buildQuizResultViewModel({
    mode,
    answers,
    questions,
    trainingContext,
    examContext,
  });
  const displayedElapsedMs = resolveDisplayedElapsedMs(timer, elapsedMs, answers);

  let scoreResult = null;
  if (mode === 'exam' && examConfig && questions.length > 0) {
    const userAnswers = answers.map((a) => ({
      questionId: a.questionId,
      answer: a.answer,
      isCorrect: a.isCorrect,
      timeSpent: a.timeSpent,
      timestamp: Date.now(),
    }));
    scoreResult = ScoreCalculator.calculate(userAnswers, questions, examConfig.scoringRules);
  }

  const resultIcon =
    resultModel.stats.accuracy >= 80 ? (
      <Sparkles className="h-7 w-7 text-success-600" />
    ) : resultModel.stats.accuracy >= 60 ? (
      <TrendingUp className="h-7 w-7 text-primary-600" />
    ) : (
      <Target className="h-7 w-7 text-warning-600" />
    );

  /** /quiz Attempt → new tab (Exam Space / result shell stays); other hrefs same-tab. */
  const openLearnerHref = (href: string) => {
    if (isQuizRuntimeHref(href)) {
      void launchQuizRuntime(href);
      return;
    }
    navigate(href);
  };

  const handlePrimaryAction = () => {
    if (resultModel.nextStep.action.href) {
      openLearnerHref(resultModel.nextStep.action.href);
      return;
    }
    onRestart();
  };

  const handleSecondaryAction = () => {
    if (resultModel.diagnosisReport) {
      navigate(resultModel.diagnosisReport.reportHref);
      return;
    }
    if (resultModel.remediationPlan.reviewCount > 0) {
      navigate(resultModel.remediationPlan.nextActionHref);
      return;
    }
    if (resultModel.learningEvidence?.reportHref) {
      navigate(resultModel.learningEvidence.reportHref);
      return;
    }
    if (resultModel.mockExamEvidence?.reportHref) {
      navigate(resultModel.mockExamEvidence.reportHref);
      return;
    }
    onRestart();
  };

  return (
    <div
      className="h-dvh overflow-y-auto overscroll-contain bg-slate-50 px-4 py-8"
      data-testid="quiz-result"
    >
      <div className="mx-auto max-w-6xl space-y-5 pb-8">
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.8fr)]">
          <Card className="overflow-hidden border-slate-200">
            <CardBody className="p-6 sm:p-8">
              <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-white ring-1 ring-slate-200">
                    {resultIcon}
                  </div>
                  <div>
                    <Badge variant={mode === 'exam' ? 'primary' : 'success'}>
                      {resultModel.modeLabel}
                    </Badge>
                    <h2 className="mt-3 text-2xl font-bold text-slate-950 sm:text-3xl">
                      {resultModel.title}
                    </h2>
                    <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                      {resultModel.summaryText}
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <Badge
                        variant={resultModel.nextStep.tone === 'warning' ? 'warning' : 'success'}
                      >
                        结果下一步
                      </Badge>
                      <span className="text-sm font-semibold text-slate-950">
                        {resultModel.nextStep.title}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-950 px-4 py-3 text-white">
                  <div className="text-xs text-slate-300">正确率</div>
                  <div className="text-3xl font-bold">{resultModel.stats.accuracy}%</div>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <Button onClick={handlePrimaryAction} size="lg" className="sm:col-span-1">
                  <ArrowRight className="h-4 w-4" />
                  {resultModel.primaryActionLabel}
                </Button>
                <Button variant="outline" onClick={handleSecondaryAction} size="lg">
                  {resultModel.secondaryActionKind === 'restart' ||
                  resultModel.secondaryActionKind === 'rewrite' ? (
                    <RotateCcw className="h-4 w-4" />
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                  {resultModel.secondaryActionLabel}
                </Button>
                <Button variant="ghost" onClick={() => navigate('/training')} size="lg">
                  回到模块选择
                </Button>
              </div>
            </CardBody>
          </Card>

          <div className="space-y-5">
            <Card>
              <CardBody className="p-5">
                <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <ClipboardList className="h-4 w-4 text-primary-600" />
                  本轮概览
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <ResultStat label="总题数" value={resultModel.stats.total} />
                  <ResultStat label="已作答" value={resultModel.stats.answered} />
                  <ResultStat label="正确" value={resultModel.stats.correct} tone="success" />
                  <ResultStat
                    label="待复盘"
                    value={resultModel.stats.wrong + resultModel.stats.unanswered}
                    tone="warning"
                  />
                  {displayedElapsedMs != null ? (
                    <ResultStat
                      label={mode === 'exam' ? '用时' : '已练'}
                      value={TimerManager.formatTime(displayedElapsedMs)}
                    />
                  ) : null}
                </div>
              </CardBody>
            </Card>

            {mode === 'exam' && scoreResult && (
              <Card>
                <CardBody className="p-5">
                  <div className="mb-2 text-sm font-semibold text-slate-900">考试评分</div>
                  <div className="text-3xl font-bold text-primary-600">
                    {scoreResult.score} / {scoreResult.totalScore}
                  </div>
                  <div className="mt-2 text-sm text-slate-600">
                    {scoreResult.passed ? (
                      <span className="inline-flex items-center gap-1 text-success-600">
                        <CheckCircle2 className="h-4 w-4" /> 通过
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-error-600">
                        <AlertTriangle className="h-4 w-4" /> 未通过
                      </span>
                    )}
                  </div>
                </CardBody>
              </Card>
            )}
          </div>
        </div>

        <Card>
          <CardBody className="p-5 sm:p-6">
            {resultModel.remediationPlan.reviewCount > 0 ? (
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary-100 bg-primary-50 px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-slate-950">待复盘</span>
                  <Badge variant="primary">{resultModel.remediationPlan.focusLabel}</Badge>
                </div>
                <div className="text-2xl font-bold text-primary-700">
                  {resultModel.remediationPlan.reviewCount}
                </div>
              </div>
            ) : null}
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-950">薄弱点优先级</h3>
                <p className="text-sm text-slate-500">先处理最集中的错题和未答题，再扩大练习量。</p>
              </div>
              <Badge variant="gray">{resultModel.weakAreas.length || 0} 个方向</Badge>
            </div>
            {resultModel.weakAreas.length > 0 ? (
              <div className="grid gap-3 md:grid-cols-3">
                {resultModel.weakAreas.map((area) => (
                  <div
                    key={area.key}
                    className="rounded-lg border border-slate-200 bg-slate-50 p-4"
                  >
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <span className="font-semibold text-slate-900">{area.label}</span>
                      <Badge variant="warning">{area.issueCount} 处</Badge>
                    </div>
                    <p className="text-sm leading-6 text-slate-600">{area.suggestion}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-success-200 bg-success-50 p-4 text-sm text-success-700">
                本轮没有发现集中薄弱点，可以进入下一组限时练习。
              </div>
            )}
          </CardBody>
        </Card>

        {resultModel.diagnosisReport && (
          <Card>
            <CardBody className="p-5 sm:p-6">
              <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-950">诊断等级与弱项</h3>
                  <p className="text-sm text-slate-500">{resultModel.diagnosisReport.levelDescription}</p>
                </div>
                <Badge variant="primary">{resultModel.diagnosisReport.levelDisplay}</Badge>
              </div>
              <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="text-sm font-semibold text-slate-950">
                  CEFR {resultModel.diagnosisReport.cefr} · 正确率{' '}
                  {resultModel.diagnosisReport.accuracyPercent}%
                </div>
                <p className="mt-1 text-sm text-slate-600">
                  {resultModel.diagnosisReport.todayTrainingReason}
                </p>
              </div>
              {resultModel.diagnosisReport.weakAreas.length > 0 ? (
                <div className="space-y-3">
                  {resultModel.diagnosisReport.weakAreas.map((area) => (
                    <div
                      key={`${area.label}-${area.total}`}
                      className="flex items-center justify-between rounded-lg border border-warning-200 bg-warning-50 px-4 py-3"
                    >
                      <div>
                        <div className="font-semibold text-slate-950">{area.label}</div>
                        <div className="text-xs text-slate-500">
                          错 {area.incorrect}/{area.total} · 正确率 {area.accuracyPercent}%
                        </div>
                      </div>
                      <Badge variant="warning">优先补</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-lg border border-success-200 bg-success-50 p-4 text-sm text-success-700">
                  本次诊断没有明显短板，可以按处方进入综合巩固。
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {resultModel.learningEvidence && (
          <Card>
            <CardBody className="p-5 sm:p-6">
              <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-950">能力报告回写</h3>
                  <p className="text-sm text-slate-500">
                    {resultModel.learningEvidence.prescriptionLabel}
                  </p>
                </div>
                <Badge variant="success">{resultModel.learningEvidence.title}</Badge>
              </div>
              <p className="text-sm leading-6 text-slate-600">
                {resultModel.learningEvidence.body}
              </p>
            </CardBody>
          </Card>
        )}

        {resultModel.mockExamEvidence && (
          <Card>
            <CardBody className="p-5 sm:p-6">
              <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-950">模考报告回写</h3>
                  <p className="text-sm text-slate-500">
                    {resultModel.mockExamEvidence.scopeLabel}
                  </p>
                </div>
                <Badge variant="success">{resultModel.mockExamEvidence.title}</Badge>
              </div>
              <p className="text-sm leading-6 text-slate-600">
                {resultModel.mockExamEvidence.body}
              </p>
            </CardBody>
          </Card>
        )}

        <Card>
          <CardBody className="p-5 sm:p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-slate-950">题目回顾</h3>
              <p className="text-sm text-slate-500">
                选择题保留对错与解析；开放作答展示评分结果，不进入错题复盘。
              </p>
            </div>
            <div className="space-y-3">
              {resultModel.reviewRows.map((row) => (
                <div key={row.id} className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-center gap-2">
                      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-100 text-sm font-semibold text-slate-700">
                        {row.index}
                      </span>
                      <Badge variant="gray">{row.type}</Badge>
                      <StatusBadge status={row.status} />
                    </div>
                  </div>
                  <div className="font-medium leading-7 text-slate-950">{row.content}</div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <AnswerBlock
                      label={row.status === 'graded' ? '你的作文' : '你的答案'}
                      value={row.userAnswer}
                      tone={
                        row.status === 'correct' || row.status === 'graded' ? 'success' : 'warning'
                      }
                    />
                    <AnswerBlock
                      label={row.status === 'graded' ? '评分' : '正确答案'}
                      value={row.correctAnswer}
                      tone="success"
                    />
                  </div>
                  {row.passage && (
                    <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm leading-6 text-slate-700">
                      <span className="font-medium text-slate-900">
                        {row.hasAudioStimulus ? '听力原文' : '材料'}
                      </span>
                      <span className="mt-1 block whitespace-pre-wrap">{row.passage}</span>
                    </div>
                  )}
                  {row.explanation && (
                    <div className="mt-3 rounded-lg bg-primary-50 p-3 text-sm leading-6 text-primary-800">
                      <span className="font-medium">解析：</span>
                      {row.explanation}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function ResultStat({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: number | string;
  tone?: 'default' | 'success' | 'warning';
}) {
  const toneClass =
    tone === 'success'
      ? 'text-success-600'
      : tone === 'warning'
        ? 'text-warning-600'
        : 'text-slate-950';

  return (
    <div className="rounded-lg bg-slate-50 p-3">
      <div className={`text-2xl font-bold ${toneClass}`}>{value}</div>
      <div className="mt-1 text-xs text-slate-500">{label}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: 'correct' | 'wrong' | 'unanswered' | 'graded' }) {
  if (status === 'correct') {
    return (
      <Badge variant="success" className="gap-1">
        <CheckCircle2 className="h-3 w-3" />
        正确
      </Badge>
    );
  }

  if (status === 'wrong') {
    return (
      <Badge variant="error" className="gap-1">
        <AlertTriangle className="h-3 w-3" />
        错误
      </Badge>
    );
  }

  if (status === 'graded') {
    return (
      <Badge variant="primary" className="gap-1">
        <Sparkles className="h-3 w-3" />
        已评分
      </Badge>
    );
  }

  return <Badge variant="warning">未答</Badge>;
}

function AnswerBlock({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'success' | 'warning';
}) {
  const className =
    tone === 'success'
      ? 'border-success-100 bg-success-50 text-success-800'
      : 'border-warning-100 bg-warning-50 text-warning-800';

  return (
    <div className={`rounded-lg border p-3 ${className}`}>
      <div className="mb-1 text-xs font-medium opacity-80">{label}</div>
      <div className="text-sm font-semibold">{value}</div>
    </div>
  );
}
